import { useState, useEffect, useCallback } from 'react';
import { Radar as RadarIcon } from 'lucide-react';
import { getSimilarityTracks, getUserTrackVehicles, searchUsers } from '../../services/api';
import DriverRadar from './DriverRadar';
import SimilarDrivers from './SimilarDrivers';
import CornerExplorer from './CornerExplorer';
import VideoSearch from './VideoSearch';
import styles from './SimilarityLabPage.module.css';

const TABS = [
  { key: 'radar',   label: 'Driver radar' },
  { key: 'similar', label: 'Similar drivers' },
  { key: 'corners', label: 'Corner explorer' },
  { key: 'video',   label: 'Video search' },
];

const DEFAULT_WEIGHTS = { speed: 1, latg: 1, long: 1, curv: 1, metrics: 1 };

// Same vocabulary as Channel Stats — best-N laps per session, by lap time.
const LAP_BASES = [
  { key: 'all',   label: 'All laps' },
  { key: 'best',  label: 'Best lap' },
  { key: 'best3', label: 'Best 3' },
  { key: 'best5', label: 'Best 5' },
];

/**
 * Similarity Lab — admin-only debug view over the corner_vectors pgvector
 * tables (driver technique radar, most-similar drivers, corner nearest-
 * neighbor explorer). Structure mirrors ChannelStatsPage; the driver search is
 * always visible since the whole page is admin-gated (App renders it only for
 * admins, and every /api/similarity endpoint requires admin).
 */
export default function SimilarityLabPage({
  currentUser, initialTrackId, onBack, onTrackChange, onLoadSession,
  onGoToLeaderboard, onGoToProgress, onGoToChannelStats, onGoToSearch,
  onOpenUserSettings, onLogout, driverProfile,
}) {
  const [tracks, setTracks] = useState([]);
  const [trackId, setTrackId] = useState(initialTrackId || null);
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState(null);   // null until the list loads
  const [tab, setTab] = useState('radar');
  const [error, setError] = useState(null);

  // Shared debug state across tabs
  const [weights, setWeights] = useState(DEFAULT_WEIGHTS);
  const [overlayUserIds, setOverlayUserIds] = useState([]);
  const [minPasses, setMinPasses] = useState(20);
  const [lapBasis, setLapBasis] = useState('all');

  // ── Viewed driver (admin impersonation — mirrors ChannelStatsPage) ────────
  const [viewUser, setViewUser] = useState(null);   // {id, name} | null (= self)
  const [driverQuery, setDriverQuery] = useState('');
  const [driverResults, setDriverResults] = useState([]);
  const viewUserId = viewUser?.id || null;

  useEffect(() => {
    if (driverQuery.trim().length < 2) { setDriverResults([]); return; }
    let cancelled = false;
    const t = setTimeout(() => {
      searchUsers(driverQuery.trim())
        .then(rs => !cancelled && setDriverResults(rs))
        .catch(() => !cancelled && setDriverResults([]));
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [driverQuery]);

  const pickDriver = useCallback((u) => {
    setViewUser({ id: u.id, name: u.display_name || u.email });
    setDriverQuery(''); setDriverResults([]);
    setOverlayUserIds([]);
  }, []);

  const clearDriver = useCallback(() => {
    setViewUser(null);
    setOverlayUserIds([]);
  }, []);

  // ── Tracks with vector coverage for the viewed driver ─────────────────────
  useEffect(() => {
    let cancelled = false;
    getSimilarityTracks(viewUserId).then(rows => {
      if (cancelled) return;
      setTracks(rows || []);
      setTrackId(prev => {
        // Keep the current track only if the *viewed* driver actually has passes
        // there. list_tracks returns every track with any coverage, by anyone, so
        // testing mere presence kept the track pinned when switching drivers and
        // every panel came up empty for someone who had never driven it.
        const prevRow = prev && rows?.find(t => t.track_id === prev);
        if (prevRow?.my_clean_passes > 0) return prev;
        const mine = rows?.find(t => t.my_clean_passes > 0);
        return mine?.track_id || rows?.[0]?.track_id || null;
      });
    }).catch(e => !cancelled && setError(String(e.message || e)));
    return () => { cancelled = true; };
  }, [viewUserId]);

  useEffect(() => { if (trackId) onTrackChange?.(trackId); }, [trackId]);  // eslint-disable-line react-hooks/exhaustive-deps

  // ── Viewed driver's vehicles at the track (most-driven first) ─────────────
  // The aggregate entity everywhere is (driver, vehicle) — a driver's two cars
  // are two separate style fingerprints, so the selector is not optional.
  useEffect(() => {
    if (!trackId) { setVehicles([]); setVehicleId(null); return; }
    let cancelled = false;
    getUserTrackVehicles(trackId, viewUserId).then(vs => {
      if (cancelled) return;
      setVehicles(vs || []);
      setVehicleId(prev => (vs?.some(v => v.id === prev) ? prev : (vs?.[0]?.id ?? '')));
      setOverlayUserIds([]);
    }).catch(() => { if (!cancelled) { setVehicles([]); setVehicleId(''); } });
    return () => { cancelled = true; };
  }, [trackId, viewUserId]);

  const track = tracks.find(t => t.track_id === trackId);

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.title}><RadarIcon size={18} /> Similarity Lab</h1>
        <span className={styles.adminTag}>admin</span>
        <div className={styles.spacer} />
        {viewUser && (
          <span className={styles.viewingChip}>
            Viewing: <strong>{viewUser.name}</strong>
            <button className={styles.chipX} onClick={clearDriver}
                    title="Back to your own data">✕</button>
          </span>
        )}
        <div className={styles.adminBox}>
          <div className={styles.driverSearch}>
            <input type="text" value={driverQuery} placeholder="View driver…"
                   onChange={e => setDriverQuery(e.target.value)} />
            {driverResults.length > 0 && (
              <div className={styles.driverResults}>
                {driverResults.map(u => (
                  <div key={u.id} className={styles.driverResult} onClick={() => pickDriver(u)}>
                    {u.display_name || u.email}
                    {u.display_name && <span className={styles.muted}> · {u.email}</span>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <div className={styles.controls}>
        <label className={styles.ctl}>
          <span>Track</span>
          <select value={trackId || ''} onChange={e => setTrackId(e.target.value || null)}>
            {tracks.map(t => (
              <option key={t.track_id} value={t.track_id}>
                {t.track_id} ({t.drivers} drivers, {t.clean_passes.toLocaleString()} passes)
              </option>
            ))}
          </select>
        </label>
        <label className={styles.ctl}>
          <span>Car</span>
          <select value={vehicleId ?? ''} onChange={e => { setVehicleId(e.target.value); setOverlayUserIds([]); }}>
            {vehicles.length === 0 && <option value="">—</option>}
            {vehicles.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
          </select>
        </label>
        {tab !== 'corners' && tab !== 'video' && (
          <label className={styles.ctl}
                 title={'Which laps feed the style aggregates, per session, '
                      + 'ranked by lap time — for everyone, not just the viewed '
                      + 'driver. "All laps" = every clean pass; "Best lap" = the '
                      + 'fastest lap of each session only. Narrower bases show '
                      + 'at-the-limit style but shrink pass counts (watch the '
                      + 'min-passes threshold).'}>
            <span>Laps</span>
            <select value={lapBasis} onChange={e => setLapBasis(e.target.value)}>
              {LAP_BASES.map(b => <option key={b.key} value={b.key}>{b.label}</option>)}
            </select>
          </label>
        )}
        <div className={styles.ctl}>
          <span>View</span>
          <div className={styles.segmented}>
            {TABS.map(t => (
              <button key={t.key}
                      className={tab === t.key ? styles.segOn : styles.seg}
                      onClick={() => setTab(t.key)}>{t.label}</button>
            ))}
          </div>
        </div>
        <div className={styles.scopeNote}>
          {track ? `${(viewUser?.name || 'you')}: ${track.my_clean_passes.toLocaleString()} clean passes here` : ''}
        </div>
      </div>

      {tab === 'radar' && (
        <DriverRadar
          trackId={trackId} viewUserId={viewUserId} vehicleId={vehicleId}
          overlayKeys={overlayUserIds} lapBasis={lapBasis}
          minPasses={minPasses} onMinPasses={setMinPasses}
        />
      )}
      {tab === 'similar' && (
        <SimilarDrivers
          trackId={trackId} viewUserId={viewUserId} vehicleId={vehicleId}
          lapBasis={lapBasis}
          weights={weights} onWeights={setWeights}
          overlayKeys={overlayUserIds} onOverlays={setOverlayUserIds}
          onShowRadar={() => setTab('radar')}
        />
      )}
      {tab === 'corners' && (
        <CornerExplorer
          trackId={trackId} viewUserId={viewUserId} vehicleId={vehicleId}
          weights={weights} onWeights={setWeights}
        />
      )}
      {tab === 'video' && (
        <VideoSearch
          onLoadSession={onLoadSession}
          viewUserId={viewUserId}
          viewUserName={viewUser?.name}
        />
      )}
    </div>
  );
}
